/**
 * Call Detail Dialog - View and edit call session details (Phase 3 Enhanced)
 *
 * Features:
 * - Session metadata (client, duration, status)
 * - Tabs for Overview/Transcript/AI Summary
 * - FULLY EDITABLE: Add/edit/delete agenda, questions, phrases, checklist
 * - Notes with action items highlighted
 * - AI-generated summary, key points, action items
 * - Copy buttons for each section
 * - Real-time sync via Convex subscriptions
 */

import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../../../convex/_generated/api';
import type { Id } from 'convex/_generated/dataModel';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
    Badge, Button, Skeleton,
    User, Clock, FileText, CheckSquare, Copy, Check, ExternalLink, Sparkles,
    MessageSquare, X, ListChecks, HelpCircle, Quote, Plus, Edit, Trash2,
} from '@uprosal/ui';

type DetailTab = 'overview' | 'transcript' | 'ai';

interface CallDetailDialogProps {
    sessionId: Id<'callSessions'> | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

// Editable section types
type EditableSection = 'agenda' | 'questions' | 'phrases' | 'checklist';

interface EditingState {
    section: EditableSection;
    index: number;
    value: string;
    whenToUse?: string; // For phrases
    group?: 'during' | 'after'; // For checklist
}

interface AddingState {
    section: EditableSection;
    value: string;
    whenToUse?: string; // For phrases
    group?: 'during' | 'after'; // For checklist
}

export function CallDetailDialog({ sessionId, open, onOpenChange }: CallDetailDialogProps) {
    const [copiedSection, setCopiedSection] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<DetailTab>('overview');

    // Edit mode state
    const [editingItem, setEditingItem] = useState<EditingState | null>(null);
    const [addingItem, setAddingItem] = useState<AddingState | null>(null);

    // Refs for auto-focus
    const editInputRef = useRef<HTMLInputElement>(null);
    const addInputRef = useRef<HTMLInputElement>(null);

    // Real-time session data from Convex
    const session = useQuery(
        api.queries.callSessions.getById,
        sessionId ? { sessionId } : 'skip'
    );

    // Mutation for updating session state
    const updateState = useMutation(api.mutations.callSessions.updateState);

    // Auto-focus inputs when edit/add mode activates
    useEffect(() => {
        if (editingItem && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editingItem]);

    useEffect(() => {
        if (addingItem && addInputRef.current) {
            addInputRef.current.focus();
        }
    }, [addingItem]);

    const handleCopy = async (text: string, section: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedSection(section);
            setTimeout(() => setCopiedSection(null), 2000);
        } catch (e) {
            console.error('Failed to copy:', e);
        }
    };

    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleString();
    };

    const formatDuration = (seconds?: number) => {
        if (!seconds) return '-';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (mins >= 60) {
            const hours = Math.floor(mins / 60);
            const remainingMins = mins % 60;
            return `${hours}h ${remainingMins}m`;
        }
        return `${mins}m ${secs}s`;
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active':
                return <Badge variant="success">Active</Badge>;
            case 'completed':
                return <Badge variant="secondary">Completed</Badge>;
            case 'archived':
                return <Badge variant="outline">Archived</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    // ============================================
    // CRUD Handlers
    // ============================================

    // Add item to a section
    const handleAddItem = async () => {
        if (!session || !sessionId || !addingItem || !addingItem.value.trim()) return;

        try {
            switch (addingItem.section) {
                case 'agenda': {
                    const newAgenda = [
                        ...(session.agendaItems || []),
                        { item: addingItem.value.trim(), done: false }
                    ];
                    await updateState({ sessionId, agendaItems: newAgenda });
                    break;
                }
                case 'questions': {
                    const newQuestions = [
                        ...(session.questions || []),
                        { text: addingItem.value.trim(), askedAt: undefined, addedToNotes: false }
                    ];
                    await updateState({ sessionId, questions: newQuestions });
                    break;
                }
                case 'phrases': {
                    const newPhrases = [
                        ...(session.phrases || []),
                        {
                            phrase: addingItem.value.trim(),
                            whenToUse: addingItem.whenToUse?.trim() || 'General use',
                            pinned: false
                        }
                    ];
                    await updateState({ sessionId, phrases: newPhrases });
                    break;
                }
                case 'checklist': {
                    const newChecklist = [
                        ...(session.checklist || []),
                        {
                            item: addingItem.value.trim(),
                            done: false,
                            group: addingItem.group || 'during'
                        }
                    ];
                    await updateState({ sessionId, checklist: newChecklist });
                    break;
                }
            }
            setAddingItem(null);
        } catch (e) {
            console.error('Failed to add item:', e);
        }
    };

    // Edit existing item
    const handleEditItem = async () => {
        if (!session || !sessionId || !editingItem || !editingItem.value.trim()) return;

        try {
            switch (editingItem.section) {
                case 'agenda': {
                    const newAgenda = session.agendaItems.map((item, i) =>
                        i === editingItem.index
                            ? { ...item, item: editingItem.value.trim() }
                            : item
                    );
                    await updateState({ sessionId, agendaItems: newAgenda });
                    break;
                }
                case 'questions': {
                    const newQuestions = session.questions.map((item, i) =>
                        i === editingItem.index
                            ? { ...item, text: editingItem.value.trim() }
                            : item
                    );
                    await updateState({ sessionId, questions: newQuestions });
                    break;
                }
                case 'phrases': {
                    const newPhrases = session.phrases.map((item, i) =>
                        i === editingItem.index
                            ? {
                                ...item,
                                phrase: editingItem.value.trim(),
                                whenToUse: editingItem.whenToUse?.trim() || item.whenToUse
                            }
                            : item
                    );
                    await updateState({ sessionId, phrases: newPhrases });
                    break;
                }
                case 'checklist': {
                    const newChecklist = session.checklist.map((item, i) =>
                        i === editingItem.index
                            ? {
                                ...item,
                                item: editingItem.value.trim(),
                                group: editingItem.group || item.group
                            }
                            : item
                    );
                    await updateState({ sessionId, checklist: newChecklist });
                    break;
                }
            }
            setEditingItem(null);
        } catch (e) {
            console.error('Failed to edit item:', e);
        }
    };

    // Delete item from section
    const handleDeleteItem = async (section: EditableSection, index: number) => {
        if (!session || !sessionId) return;

        try {
            switch (section) {
                case 'agenda': {
                    const newAgenda = session.agendaItems.filter((_, i) => i !== index);
                    await updateState({ sessionId, agendaItems: newAgenda });
                    break;
                }
                case 'questions': {
                    const newQuestions = session.questions.filter((_, i) => i !== index);
                    await updateState({ sessionId, questions: newQuestions });
                    break;
                }
                case 'phrases': {
                    const newPhrases = session.phrases.filter((_, i) => i !== index);
                    await updateState({ sessionId, phrases: newPhrases });
                    break;
                }
                case 'checklist': {
                    const newChecklist = session.checklist.filter((_, i) => i !== index);
                    await updateState({ sessionId, checklist: newChecklist });
                    break;
                }
            }
        } catch (e) {
            console.error('Failed to delete item:', e);
        }
    };

    // Toggle handlers (existing)
    const handleToggleChecklistItem = async (itemIndex: number, currentDone: boolean) => {
        if (!session || !sessionId) return;
        const updatedChecklist = session.checklist.map((item, idx) =>
            idx === itemIndex ? { ...item, done: !currentDone } : item
        );
        try {
            await updateState({ sessionId, checklist: updatedChecklist });
        } catch (e) {
            console.error('Failed to update checklist:', e);
        }
    };

    const handleToggleAgendaItem = async (itemIndex: number, currentDone: boolean) => {
        if (!session || !sessionId) return;
        const updatedAgenda = session.agendaItems.map((item, idx) =>
            idx === itemIndex ? { ...item, done: !currentDone } : item
        );
        try {
            await updateState({ sessionId, agendaItems: updatedAgenda });
        } catch (e) {
            console.error('Failed to update agenda:', e);
        }
    };

    // Mark question as asked
    const handleToggleQuestionAsked = async (itemIndex: number, currentAsked: number | undefined) => {
        if (!session || !sessionId) return;
        const updatedQuestions = session.questions.map((item, idx) =>
            idx === itemIndex
                ? { ...item, askedAt: currentAsked ? undefined : Date.now() }
                : item
        );
        try {
            await updateState({ sessionId, questions: updatedQuestions });
        } catch (e) {
            console.error('Failed to update question:', e);
        }
    };

    // ============================================
    // Text generation for copy
    // ============================================

    const getNotesText = () => {
        if (!session?.noteLines) return '';
        return session.noteLines.map(n => n.text).join('\n');
    };

    const getActionItemsText = () => {
        if (!session?.noteLines) return '';
        return session.noteLines
            .filter(n => n.isActionItem)
            .map(n => `- ${n.text}`)
            .join('\n');
    };

    const getChecklistText = () => {
        if (!session?.checklist) return '';
        const during = session.checklist.filter(c => c.group === 'during');
        const after = session.checklist.filter(c => c.group === 'after');

        let text = 'During Call:\n';
        text += during.map(c => `[${c.done ? 'x' : ' '}] ${c.item}`).join('\n');
        text += '\n\nAfter Call:\n';
        text += after.map(c => `[${c.done ? 'x' : ' '}] ${c.item}`).join('\n');
        return text;
    };

    const getAgendaText = () => {
        if (!session?.agendaItems) return '';
        return session.agendaItems.map(a => `[${a.done ? 'x' : ' '}] ${a.item}`).join('\n');
    };

    const getQuestionsText = () => {
        if (!session?.questions) return '';
        return session.questions.map(q => `${q.askedAt ? '[Asked] ' : ''}${q.text}`).join('\n');
    };

    const actionItems = session?.noteLines?.filter(n => n.isActionItem) ?? [];
    const duringChecklist = session?.checklist?.filter(c => c.group === 'during') ?? [];
    const afterChecklist = session?.checklist?.filter(c => c.group === 'after') ?? [];

    // ============================================
    // Inline Add Form Component
    // ============================================
    const InlineAddForm = ({
        section,
        placeholder,
        showWhenToUse = false,
        showGroup = false
    }: {
        section: EditableSection;
        placeholder: string;
        showWhenToUse?: boolean;
        showGroup?: boolean;
    }) => {
        if (addingItem?.section !== section) {
            return (
                <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-muted-foreground hover:text-foreground mt-2"
                    onClick={() => setAddingItem({ section, value: '', whenToUse: '', group: 'during' })}
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add {section === 'agenda' ? 'item' : section === 'questions' ? 'question' : section === 'phrases' ? 'phrase' : 'item'}
                </Button>
            );
        }

        return (
            <div className="mt-2 p-2 bg-muted/50 rounded-lg space-y-2">
                <input
                    ref={addInputRef}
                    type="text"
                    value={addingItem.value}
                    onChange={(e) => setAddingItem({ ...addingItem, value: e.target.value })}
                    placeholder={placeholder}
                    className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleAddItem();
                        } else if (e.key === 'Escape') {
                            setAddingItem(null);
                        }
                    }}
                />
                {showWhenToUse && (
                    <input
                        type="text"
                        value={addingItem.whenToUse || ''}
                        onChange={(e) => setAddingItem({ ...addingItem, whenToUse: e.target.value })}
                        placeholder="When to use (e.g., 'For pricing discussions')"
                        className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                )}
                {showGroup && (
                    <select
                        value={addingItem.group || 'during'}
                        onChange={(e) => setAddingItem({ ...addingItem, group: e.target.value as 'during' | 'after' })}
                        className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        <option value="during">During Call</option>
                        <option value="after">After Call</option>
                    </select>
                )}
                <div className="flex gap-2 justify-end">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setAddingItem(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleAddItem}
                        disabled={!addingItem.value.trim()}
                    >
                        Add
                    </Button>
                </div>
            </div>
        );
    };

    // ============================================
    // Editable Item Component
    // ============================================
    const EditableItem = ({
        section,
        index,
        value,
        whenToUse,
        group,
        renderContent,
        className = ''
    }: {
        section: EditableSection;
        index: number;
        value: string;
        whenToUse?: string;
        group?: 'during' | 'after';
        renderContent: () => React.ReactNode;
        className?: string;
    }) => {
        const isEditing = editingItem?.section === section && editingItem?.index === index;

        if (isEditing) {
            return (
                <div className="p-2 bg-muted/50 rounded-lg space-y-2">
                    <input
                        ref={editInputRef}
                        type="text"
                        value={editingItem.value}
                        onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleEditItem();
                            } else if (e.key === 'Escape') {
                                setEditingItem(null);
                            }
                        }}
                    />
                    {section === 'phrases' && (
                        <input
                            type="text"
                            value={editingItem.whenToUse || ''}
                            onChange={(e) => setEditingItem({ ...editingItem, whenToUse: e.target.value })}
                            placeholder="When to use"
                            className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                    )}
                    {section === 'checklist' && (
                        <select
                            value={editingItem.group || 'during'}
                            onChange={(e) => setEditingItem({ ...editingItem, group: e.target.value as 'during' | 'after' })}
                            className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                            <option value="during">During Call</option>
                            <option value="after">After Call</option>
                        </select>
                    )}
                    <div className="flex gap-2 justify-end">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingItem(null)}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleEditItem}
                            disabled={!editingItem.value.trim()}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            );
        }

        return (
            <div className={`group flex items-start gap-2 ${className}`}>
                <div className="flex-1">{renderContent()}</div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => setEditingItem({ section, index, value, whenToUse, group })}
                    >
                        <Edit className="w-3 h-3" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteItem(section, index)}
                    >
                        <Trash2 className="w-3 h-3" />
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader className="relative">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 rounded-full p-1.5 h-auto"
                        onClick={() => onOpenChange(false)}
                    >
                        <X className="w-4 h-4" />
                        <span className="sr-only">Close</span>
                    </Button>
                    <DialogTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Call Session Details
                    </DialogTitle>
                    <DialogDescription>
                        View and edit notes, action items, and checklist from this call
                    </DialogDescription>
                </DialogHeader>

                {!session ? (
                    <div className="space-y-4">
                        <Skeleton className="h-8 w-1/3" />
                        <Skeleton className="h-32 w-full" />
                        <Skeleton className="h-24 w-full" />
                    </div>
                ) : (
                    <div className="space-y-6 mt-4">
                        {/* Session Info */}
                        <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-muted-foreground" />
                                <span className="font-medium">{session.clientName}</span>
                            </div>
                            {getStatusBadge(session.status)}
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                {formatDate(session.createdAt)}
                            </div>
                            {session.duration && (
                                <div className="text-sm text-muted-foreground">
                                    Duration: {formatDuration(session.duration)}
                                </div>
                            )}
                            {session.sourceUrl && (
                                <Button variant="ghost" size="sm" asChild>
                                    <a href={session.sourceUrl} target="_blank" rel="noopener noreferrer">
                                        <ExternalLink className="w-3 h-3 mr-1" />
                                        Source
                                    </a>
                                </Button>
                            )}
                        </div>

                        {/* Tabs */}
                        <div className="flex gap-1 bg-muted/50 p-1 rounded-lg">
                            <Button
                                variant={activeTab === 'overview' ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setActiveTab('overview')}
                                className="text-sm"
                            >
                                <FileText className="w-4 h-4 mr-1.5" />
                                Overview
                            </Button>
                            <Button
                                variant={activeTab === 'transcript' ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setActiveTab('transcript')}
                                className="text-sm"
                            >
                                <MessageSquare className="w-4 h-4 mr-1.5" />
                                Transcript
                            </Button>
                            <Button
                                variant={activeTab === 'ai' ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setActiveTab('ai')}
                                className="text-sm"
                            >
                                <Sparkles className="w-4 h-4 mr-1.5" />
                                AI Insights
                            </Button>
                        </div>

                        {/* Overview Tab Content */}
                        {activeTab === 'overview' && (
                            <>
                                {/* Agenda Section - EDITABLE */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-medium flex items-center gap-2">
                                            <ListChecks className="w-4 h-4" />
                                            Agenda
                                            <Badge variant="secondary" className="text-xs">
                                                {session.agendaItems?.filter(a => a.done).length || 0}/{session.agendaItems?.length || 0}
                                            </Badge>
                                        </h3>
                                        {session.agendaItems && session.agendaItems.length > 0 && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleCopy(getAgendaText(), 'agenda')}
                                            >
                                                {copiedSection === 'agenda' ? (
                                                    <Check className="w-4 h-4 text-primary" />
                                                ) : (
                                                    <Copy className="w-4 h-4" />
                                                )}
                                                <span className="ml-1">Copy</span>
                                            </Button>
                                        )}
                                    </div>
                                    <div className="space-y-1 p-3 bg-muted/30 rounded-lg">
                                        {session.agendaItems && session.agendaItems.length > 0 ? (
                                            session.agendaItems.map((item, idx) => (
                                                <EditableItem
                                                    key={idx}
                                                    section="agenda"
                                                    index={idx}
                                                    value={item.item}
                                                    className="text-sm cursor-pointer hover:bg-muted/50 p-1 rounded"
                                                    renderContent={() => (
                                                        <div
                                                            className="flex items-center gap-2"
                                                            onClick={() => handleToggleAgendaItem(idx, item.done)}
                                                        >
                                                            <div className={`w-4 h-4 rounded border flex items-center justify-center ${item.done ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                                                                {item.done && <Check className="w-3 h-3 text-primary-foreground" />}
                                                            </div>
                                                            <span className={item.done ? 'line-through text-muted-foreground' : ''}>
                                                                {item.item}
                                                            </span>
                                                        </div>
                                                    )}
                                                />
                                            ))
                                        ) : (
                                            <p className="text-sm text-muted-foreground">No agenda items</p>
                                        )}
                                        <InlineAddForm section="agenda" placeholder="Add agenda item..." />
                                    </div>
                                </div>

                                {/* Questions Section - EDITABLE */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-medium flex items-center gap-2">
                                            <HelpCircle className="w-4 h-4" />
                                            Questions
                                            <Badge variant="secondary" className="text-xs">
                                                {session.questions?.filter(q => q.askedAt).length || 0}/{session.questions?.length || 0} asked
                                            </Badge>
                                        </h3>
                                        {session.questions && session.questions.length > 0 && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleCopy(getQuestionsText(), 'questions')}
                                            >
                                                {copiedSection === 'questions' ? (
                                                    <Check className="w-4 h-4 text-primary" />
                                                ) : (
                                                    <Copy className="w-4 h-4" />
                                                )}
                                                <span className="ml-1">Copy</span>
                                            </Button>
                                        )}
                                    </div>
                                    <div className="space-y-2 p-3 bg-muted/30 rounded-lg">
                                        {session.questions && session.questions.length > 0 ? (
                                            session.questions.map((q, idx) => (
                                                <EditableItem
                                                    key={idx}
                                                    section="questions"
                                                    index={idx}
                                                    value={q.text}
                                                    className="text-sm"
                                                    renderContent={() => (
                                                        <div
                                                            className="flex items-start gap-2 cursor-pointer"
                                                            onClick={() => handleToggleQuestionAsked(idx, q.askedAt)}
                                                        >
                                                            <span className="text-muted-foreground shrink-0">-</span>
                                                            <span className={q.askedAt ? 'text-muted-foreground' : ''}>
                                                                {q.text}
                                                                {q.askedAt && (
                                                                    <Badge variant="outline" className="ml-2 text-xs">Asked</Badge>
                                                                )}
                                                            </span>
                                                        </div>
                                                    )}
                                                />
                                            ))
                                        ) : (
                                            <p className="text-sm text-muted-foreground">No questions</p>
                                        )}
                                        <InlineAddForm section="questions" placeholder="Add question to ask..." />
                                    </div>
                                </div>

                                {/* Quick Phrases Section - EDITABLE */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-medium flex items-center gap-2">
                                            <Quote className="w-4 h-4" />
                                            Quick Phrases
                                            <Badge variant="secondary" className="text-xs">
                                                {session.phrases?.length || 0}
                                            </Badge>
                                        </h3>
                                    </div>
                                    <div className="space-y-2 p-3 bg-muted/30 rounded-lg">
                                        {session.phrases && session.phrases.length > 0 ? (
                                            session.phrases.map((p, idx) => (
                                                <EditableItem
                                                    key={idx}
                                                    section="phrases"
                                                    index={idx}
                                                    value={p.phrase}
                                                    whenToUse={p.whenToUse}
                                                    className="text-sm"
                                                    renderContent={() => (
                                                        <div>
                                                            <p className="font-medium">"{p.phrase}"</p>
                                                            <p className="text-xs text-muted-foreground">{p.whenToUse}</p>
                                                        </div>
                                                    )}
                                                />
                                            ))
                                        ) : (
                                            <p className="text-sm text-muted-foreground">No quick phrases</p>
                                        )}
                                        <InlineAddForm
                                            section="phrases"
                                            placeholder="Add quick phrase..."
                                            showWhenToUse
                                        />
                                    </div>
                                </div>

                                {/* Notes Section */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-medium flex items-center gap-2">
                                            <FileText className="w-4 h-4" />
                                            Notes
                                            {session.noteLines && session.noteLines.length > 0 && (
                                                <Badge variant="secondary" className="text-xs">
                                                    {session.noteLines.length}
                                                </Badge>
                                            )}
                                        </h3>
                                        {session.noteLines && session.noteLines.length > 0 && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleCopy(getNotesText(), 'notes')}
                                            >
                                                {copiedSection === 'notes' ? (
                                                    <Check className="w-4 h-4 text-primary" />
                                                ) : (
                                                    <Copy className="w-4 h-4" />
                                                )}
                                                <span className="ml-1">Copy</span>
                                            </Button>
                                        )}
                                    </div>
                                    {session.noteLines && session.noteLines.length > 0 ? (
                                        <div className="space-y-2 p-3 bg-muted/30 rounded-lg">
                                            {session.noteLines.map((note) => (
                                                <div
                                                    key={note.id}
                                                    className={`text-sm ${note.isActionItem ? 'text-primary font-medium' : ''}`}
                                                >
                                                    {note.isActionItem && '- '}
                                                    {note.text}
                                                    {note.timestamp && (
                                                        <span className="text-xs text-muted-foreground ml-2">
                                                            [{formatDuration(note.timestamp)}]
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">No notes recorded</p>
                                    )}
                                </div>

                                {/* Action Items Section */}
                                {actionItems.length > 0 && (
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className="font-medium flex items-center gap-2">
                                                <CheckSquare className="w-4 h-4" />
                                                Action Items
                                                <Badge variant="success" className="text-xs">
                                                    {actionItems.length}
                                                </Badge>
                                            </h3>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleCopy(getActionItemsText(), 'actions')}
                                            >
                                                {copiedSection === 'actions' ? (
                                                    <Check className="w-4 h-4 text-primary" />
                                                ) : (
                                                    <Copy className="w-4 h-4" />
                                                )}
                                                <span className="ml-1">Copy</span>
                                            </Button>
                                        </div>
                                        <div className="space-y-1 p-3 bg-primary/5 rounded-lg border border-primary/20">
                                            {actionItems.map((item) => (
                                                <div key={item.id} className="text-sm text-primary flex items-start gap-2">
                                                    <span className="shrink-0">-</span>
                                                    <span>{item.text}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Checklist Section - EDITABLE */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-medium flex items-center gap-2">
                                            <CheckSquare className="w-4 h-4" />
                                            Checklist
                                        </h3>
                                        {session.checklist && session.checklist.length > 0 && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleCopy(getChecklistText(), 'checklist')}
                                            >
                                                {copiedSection === 'checklist' ? (
                                                    <Check className="w-4 h-4 text-primary" />
                                                ) : (
                                                    <Copy className="w-4 h-4" />
                                                )}
                                                <span className="ml-1">Copy</span>
                                            </Button>
                                        )}
                                    </div>
                                    <div className="space-y-4">
                                        {/* During Call */}
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase font-medium mb-2">
                                                During Call
                                            </p>
                                            <div className="space-y-1 p-3 bg-muted/30 rounded-lg">
                                                {duringChecklist.length > 0 ? (
                                                    session.checklist?.map((item, idx) => item.group === 'during' && (
                                                        <EditableItem
                                                            key={idx}
                                                            section="checklist"
                                                            index={idx}
                                                            value={item.item}
                                                            group={item.group}
                                                            className="text-sm cursor-pointer hover:bg-muted/50 p-1 rounded"
                                                            renderContent={() => (
                                                                <div
                                                                    className="flex items-center gap-2"
                                                                    onClick={() => handleToggleChecklistItem(idx, item.done)}
                                                                >
                                                                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${item.done ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                                                                        {item.done && <Check className="w-3 h-3 text-primary-foreground" />}
                                                                    </div>
                                                                    <span className={item.done ? 'line-through text-muted-foreground' : ''}>
                                                                        {item.item}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        />
                                                    ))
                                                ) : (
                                                    <p className="text-sm text-muted-foreground">No items</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* After Call */}
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase font-medium mb-2">
                                                After Call
                                            </p>
                                            <div className="space-y-1 p-3 bg-muted/30 rounded-lg">
                                                {afterChecklist.length > 0 ? (
                                                    session.checklist?.map((item, idx) => item.group === 'after' && (
                                                        <EditableItem
                                                            key={idx}
                                                            section="checklist"
                                                            index={idx}
                                                            value={item.item}
                                                            group={item.group}
                                                            className="text-sm cursor-pointer hover:bg-muted/50 p-1 rounded"
                                                            renderContent={() => (
                                                                <div
                                                                    className="flex items-center gap-2"
                                                                    onClick={() => handleToggleChecklistItem(idx, item.done)}
                                                                >
                                                                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${item.done ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                                                                        {item.done && <Check className="w-3 h-3 text-primary-foreground" />}
                                                                    </div>
                                                                    <span className={item.done ? 'line-through text-muted-foreground' : ''}>
                                                                        {item.item}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        />
                                                    ))
                                                ) : (
                                                    <p className="text-sm text-muted-foreground">No items</p>
                                                )}
                                            </div>
                                        </div>

                                        <InlineAddForm
                                            section="checklist"
                                            placeholder="Add checklist item..."
                                            showGroup
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Transcript Tab Content */}
                        {activeTab === 'transcript' && (
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="font-medium flex items-center gap-2">
                                        <MessageSquare className="w-4 h-4" />
                                        Transcript
                                    </h3>
                                    {session.transcript && session.transcript.length > 0 && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleCopy(
                                                session.transcript?.map(t => `[${t.speaker}] ${t.text}`).join('\n') || '',
                                                'transcript'
                                            )}
                                        >
                                            {copiedSection === 'transcript' ? (
                                                <Check className="w-4 h-4 text-primary" />
                                            ) : (
                                                <Copy className="w-4 h-4" />
                                            )}
                                            <span className="ml-1">Copy</span>
                                        </Button>
                                    )}
                                </div>
                                {session.transcript && session.transcript.length > 0 ? (
                                    <div className="space-y-3 p-3 bg-muted/30 rounded-lg max-h-96 overflow-y-auto">
                                        {session.transcript.map((entry, idx) => (
                                            <div key={idx} className="flex gap-3">
                                                <div className="flex-shrink-0">
                                                    <Badge variant={entry.speaker === 'You' ? 'secondary' : 'outline'}>
                                                        {entry.speaker}
                                                    </Badge>
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm">{entry.text}</p>
                                                    {entry.timestamp && (
                                                        <span className="text-xs text-muted-foreground">
                                                            {entry.timestamp}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">No transcript available</p>
                                        <p className="text-xs mt-1">
                                            Enable transcription during calls to see speaker-labeled transcripts here.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* AI Insights Tab Content */}
                        {activeTab === 'ai' && (
                            <div className="space-y-6">
                                {/* AI Summary */}
                                <div>
                                    <h3 className="font-medium flex items-center gap-2 mb-2">
                                        <Sparkles className="w-4 h-4 text-primary" />
                                        AI Summary
                                    </h3>
                                    {session.aiSummary ? (
                                        <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                                            <p className="text-sm">{session.aiSummary}</p>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">
                                            No AI summary generated. Use "Extract with AI" in Call Companion after a call.
                                        </p>
                                    )}
                                </div>

                                {/* AI Key Points */}
                                <div>
                                    <h3 className="font-medium flex items-center gap-2 mb-2">
                                        <Sparkles className="w-4 h-4 text-primary" />
                                        Key Points
                                    </h3>
                                    {session.aiKeyPoints && session.aiKeyPoints.length > 0 ? (
                                        <div className="space-y-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
                                            {session.aiKeyPoints.map((point, idx) => (
                                                <div key={idx} className="flex items-start gap-2 text-sm">
                                                    <span className="text-primary">-</span>
                                                    <span>{point}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">
                                            No key points extracted yet.
                                        </p>
                                    )}
                                </div>

                                {/* AI Action Items */}
                                <div>
                                    <h3 className="font-medium flex items-center gap-2 mb-2">
                                        <Sparkles className="w-4 h-4 text-primary" />
                                        AI-Extracted Action Items
                                    </h3>
                                    {session.aiActionItems && session.aiActionItems.length > 0 ? (
                                        <div className="space-y-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
                                            {session.aiActionItems.map((action, idx) => (
                                                <div key={idx} className="flex items-start gap-2 text-sm">
                                                    <CheckSquare className="w-4 h-4 text-primary mt-0.5" />
                                                    <span>{action}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">
                                            No AI action items extracted yet.
                                        </p>
                                    )}
                                </div>

                                {!session.aiSummary && !session.aiKeyPoints?.length && !session.aiActionItems?.length && (
                                    <div className="text-center py-8 text-muted-foreground border-2 border-dashed border-muted rounded-lg">
                                        <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">No AI insights generated for this call</p>
                                        <p className="text-xs mt-1">
                                            Use "Extract with AI" in Call Companion during or after a call.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
